import React, { memo, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSetAtom, useAtom } from 'jotai';
import {
  categoryMetaAtom,
  categoryMetaKeysAtom,
  categoryMetaOrderAtom,
  configAtom,
  flowDataAtom,
  tempKPIAtom,
  defaultFlowConfig,
  allSelectedPresentAtom,
} from './atoms';
import { Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import {
  combineWithMetricDeDuplication,
  excludeWithMetricDeDuplication,
  getPercentageMetricByKey,
} from '@utils/metrics/metricHelpers';
import { metricsStore } from '@globalStore/metricsStore';
import { METRIC_POPUP_TITLE, METRIC_POPUP_OK_TEXT, isAppModule } from '@utils/constants';
import useMetricsMeta from '@hooks/useMetricsMeta';
import { clearCache } from '@src/api/cacheAdapter/config';
import useMetricCategoryOrder from './hooks/useMetricCategoryOrder';
import MetricPopupModal from './components/MetricPopupModal';

const orderedSort = (unorderedList, orderedList) =>
  unorderedList.sort((a, b) => orderedList.indexOf(a) - orderedList.indexOf(b));

const MetricSelectorPopup = ({
  flowData,
  supportedMetrics,
  selectedMetrics,
  lockedMetrics = [], // array of metrics, to be checked and disabled
  buttonText = 'Metrics',
  onApply,
  metricsCountLimit = 999,
  RenderBtn = Button,
  config,
  currentKpiId,
  onKpiChange,
  allowDimensionApply = false,
}: {
  allowDimensionApply?: boolean;
  flowData?: any;
  supportedMetrics: Record<string, any>;
  selectedMetrics: any[];
  lockedMetrics?: string[];
  buttonText?: string;
  onApply: (metrics: any[], kpiChanged: boolean, applyToDimensions?: boolean) => void;
  metricsCountLimit?: number;
  RenderBtn?: React.ElementType;
  config?: any;
  currentKpiId?: number | null;
  onKpiChange?: (kpiId: number) => void;
}) => {
  const { metricsCategoryMeta, metricsCategoryKeys } = metricsStore.getState();
  const searchRef: any = useRef(null);
  const [selectedMetricList, setSelectedMetrics] = useState<string[]>([]);
  const [tempKpi, setTempKpi] = useAtom(tempKPIAtom);
  const productCategoryOrder = useMetricCategoryOrder(supportedMetrics);
  const [visible, setVisible] = useState(false);
  const [applyToDimensions, setApplyToDimensions] = useState(false);
  const invalidDraft =
    selectedMetricList.length === 0 ||
    (allowDimensionApply && applyToDimensions && selectedMetricList.length > 20);
  const isECOContext = isAppModule;
  const setCategoryOrder = useSetAtom(categoryMetaOrderAtom);
  const setCategoryMeta = useSetAtom(categoryMetaAtom);
  const setCategoryKeys = useSetAtom(categoryMetaKeysAtom);
  const setFlowData = useSetAtom(flowDataAtom);
  const setConfigAtom = useSetAtom(configAtom);
  const setAllSelectedPresent = useSetAtom(allSelectedPresentAtom);

  useEffect(() => {
    setCategoryMeta(metricsCategoryMeta);
    setCategoryKeys(metricsCategoryKeys);
    setCategoryOrder(productCategoryOrder);
    setFlowData(flowData || defaultFlowConfig);
    setConfigAtom(config);
  }, [metricsCategoryMeta, metricsCategoryKeys, productCategoryOrder, flowData, config]);

  useEffect(() => {
    setSelectedMetrics(selectedMetrics);
  }, [selectedMetrics]);

  const {
    forceUpdateUserDefinedMetricsMeta,
    isUserDefinedMetricsLoading,
    forceUpdateFlowConfigMeta,
    isFlowConfigMetaLoading,
  } = useMetricsMeta();

  useEffect(() => {
    // Sync tempKpi from persisted currentKpiId (initialization is handled by useKPI hook)
    if (currentKpiId) {
      setTempKpi(currentKpiId);
    }
  }, [currentKpiId, setTempKpi]);

  useEffect(() => {
    if (selectedMetricList.length > metricsCountLimit) {
      message.warning(`You can select a maximum of ${metricsCountLimit} metrics.`);
      // TODO: slicing should consider both secondary and order by metric
      setSelectedMetrics(selectedMetricList.slice(0, metricsCountLimit));
    }
    if (!isECOContext) {
      const checkCategoryList: any = [];
      Object.values(supportedMetrics).forEach((metricList: any) => {
        const metricKeyList = Object.values(metricList).map((metric: any) => metric.name);
        const checkMetric = metricKeyList.every((val) => selectedMetricList.includes(val));
        checkCategoryList.push(checkMetric);
      });
      const hasTrue = checkCategoryList.some(Boolean);
      setAllSelectedPresent(hasTrue);
    }
  }, [selectedMetricList]);

  const lockedMetricsSet = useMemo(() => {
    const metricSet = new Set<string>();
    (allowDimensionApply ? [] : lockedMetrics).forEach((m) => {
      metricSet.add(m);
      const invertedMetric = getPercentageMetricByKey({ metricKey: m });
      if (invertedMetric) {
        metricSet.add(invertedMetric);
      }
    });
    return metricSet;
  }, [lockedMetrics, allowDimensionApply]);

  const refetchUserDefinedMetrics = useCallback(() => {
    forceUpdateUserDefinedMetricsMeta();
    forceUpdateFlowConfigMeta();
  }, []);

  const openPopup = () => {
    setApplyToDimensions(false);
    setSelectedMetrics(selectedMetrics);
    setVisible(true);

    // immediately calling this action causes a render issue during transition animation
    // making it jittery on dark mode
    setTimeout(() => {
      // focus on search box after a delay
      // ref init + visual feedback after popup animation
      if (searchRef.current) {
        searchRef.current.focus({
          cursor: 'start',
        });
      }
      refetchUserDefinedMetrics();
    }, 500);
  };

  const closePopup = () => {
    setVisible(false);
    setSelectedMetrics(selectedMetrics); // restore select metrics
    if (currentKpiId) {
      setTempKpi(currentKpiId);
    }
  };

  const applyMetrics = () => {
    if (selectedMetricList && selectedMetricList.length > 0) {
      setVisible(false);
      if (!allowDimensionApply && productCategoryOrder.length > 0) {
        orderedSort(selectedMetricList, productCategoryOrder);
      }
      onApply(
        [...selectedMetricList],
        tempKpi !== currentKpiId,
        allowDimensionApply && applyToDimensions,
      );
    } else {
      message.warning('Please select a metric.');
    }
  };

  const onOk = () => {
    if (allowDimensionApply && invalidDraft) return;
    if (tempKpi !== currentKpiId) {
      // Notify parent about KPI change (now just passing the ID)
      if (onKpiChange) {
        onKpiChange(tempKpi);
      }

      // Clear cache as KPI changed
      clearCache();
      applyMetrics();
    } else {
      applyMetrics();
    }
  };

  // clear all selected metrics
  const onClear = () => {
    // do not remove currently selected metric on trends
    setSelectedMetrics(allowDimensionApply ? [] : lockedMetrics);
  };

  // preventing the rendering if we don't have the dimension list

  const onChange = (item) => {
    let listOfMetrics: any[] = [];
    const metricName = item.target.value;
    listOfMetrics.push(...selectedMetricList);

    if (listOfMetrics.includes(metricName)) {
      // uncheck action
      listOfMetrics = listOfMetrics.filter((value) => value !== metricName);
    } else {
      // check action
      listOfMetrics.push(metricName);
    }
    setSelectedMetrics(listOfMetrics);
  };

  // category level checkbox action
  const onBulkChange = (checked, inputMetricList) => {
    let listOfMetrics: any[] = [];

    if (checked) {
      listOfMetrics = combineWithMetricDeDuplication(selectedMetricList, inputMetricList);
    } else {
      // do not uncheck selected metric
      const filteredMetrics = inputMetricList.filter((m) => !lockedMetricsSet.has(m));
      listOfMetrics = excludeWithMetricDeDuplication(selectedMetricList, filteredMetrics);
    }
    setSelectedMetrics(listOfMetrics);
  };

  if (isEmpty(supportedMetrics)) {
    return null;
  }

  return (
    <>
      <RenderBtn onClick={openPopup} icon={<PlusOutlined />}>
        {buttonText}
      </RenderBtn>
      <MetricPopupModal
        allowDimensionApply={allowDimensionApply}
        applyToDimensions={applyToDimensions}
        onApplyToDimensionsChange={setApplyToDimensions}
        applyDisabled={allowDimensionApply && invalidDraft}
        metricCategories={supportedMetrics}
        title={METRIC_POPUP_TITLE}
        visible={visible}
        selectedMetricList={selectedMetricList}
        onCancel={closePopup}
        lockedMetricsSet={lockedMetricsSet}
        onOk={onOk}
        okText={METRIC_POPUP_OK_TEXT}
        onChange={onChange}
        onClear={onClear}
        onBulkChange={onBulkChange}
        searchRef={searchRef}
        isLoading={isECOContext && (isUserDefinedMetricsLoading || isFlowConfigMetaLoading)}
      />
    </>
  );
};

export default memo(MetricSelectorPopup);
